<template>
  <ImaskInput
    :model-value="displayValue"
    class="input"
    :class="{'faded': faded}"
    :mask="mask"
    :placeholder="placeholder"
    :readonly="readonly"
    @accept="input"
    @blur="blur"
    @keydown="handleKeydown"
    @paste="paste"
  />
</template>

<script>
import _ from "lodash";
import BigNumber from "bignumber.js";
import { IMaskComponent } from "vue-imask";

export default {
  name: "AuDecimalInput",

  components: {
    "ImaskInput": IMaskComponent
  },

  props: {
    value: {
      type: String,
      default: ""
    },

    decimal: {
      type: Number,
      default: 0
    },

    indent: {
      type: Number,
      default: 2
    },

    placeholder: {
      type: String,
      default: ""
    },

    readonly: {
      type: Boolean,
      default: false
    },

    faded: {
      type: Boolean,
      default: false
    },

    max: {
      type: Number,
      default: null
    },

    min: {
      type: Number,
      default: null
    }
  },

  emits: ["input"],

  computed: {
    mask() {
      return new RegExp(`^\\d*\\.?\\d{0,${this.indent}}$`);
    },

    maxResult() {
      if (!this.max) {
        return null;
      }
      if (!this.decimal) {
        return this.max;
      }
      const num = new BigNumber(this.max);
      const divisor = new BigNumber(10).pow(this.decimal);
      return num.dividedBy(divisor).toNumber();
    },

    minResult() {
      if (!this.min) {
        return null;
      }
      if (!this.decimal) {
        return this.min;
      }
      const num = new BigNumber(this.min);
      const divisor = new BigNumber(10).pow(this.decimal);
      return num.dividedBy(divisor).toNumber();
    },

    displayValue() {
      if (this.value == "" || this.value == null || _.isNaN(this.value)) {
        return "";
      }

      let value = this.value;
      if (typeof value == "string") {
        value = value.replace(",", ".");
      }

      if (this.decimal) {
        value = BigNumber(value).dividedBy(BigNumber(10).pow(this.decimal));
        value = value.isNaN() ? "" : value;
      }

      return value.toString();
    }
  },

  methods: {
    paste(e) {
      if (!this.maxResult) {
        return;
      }

      e.preventDefault();
      const clipboardData = e.clipboardData || window.clipboardData;
      const pastedText = clipboardData.getData("text");

      if (!pastedText) {
        return;
      }

      let processedValue = pastedText.replace(/[^\d.,]/g, "").replace(",", ".");

      if (processedValue && BigNumber(processedValue).gt(this.maxResult)) {
        processedValue = this.maxResult.toString();
      }

      document.execCommand("insertText", false, processedValue);
    },

    blur() {
      if (this.value !== "") {
        const value = new BigNumber(this.value);

        if (this.min && value.isLessThan(this.min)) {
          this.$emit("input", new BigNumber(this.min).toString());
        }

        if (this.max && value.isGreaterThan(this.max)) {
          this.$emit("input", new BigNumber(this.max).toString());
        }
      }
    },

    handleKeydown(e) {
      const input = e.target;
      const hasSelection = input.selectionStart !== input.selectionEnd;

      if (!this.maxResult) {
        return;
      }

      if (BigNumber(this.value).eq(this.max) && !hasSelection) {
        if (e.keyCode >= 48 && e.keyCode <= 57 ||
            e.keyCode >= 96 && e.keyCode <= 105) {
          e.preventDefault();
          return;
        }
      }
    },

    input(value) {
      if (value !== "") {
        let bnValue = new BigNumber(value);

        if (this.maxResult && bnValue.isGreaterThan(this.maxResult)) {
          bnValue = new BigNumber(this.maxResult);
        }

        if (this.decimal) {
          const multiplier = new BigNumber(10).exponentiatedBy(this.decimal);
          bnValue = bnValue.multipliedBy(multiplier);
        }

        this.$emit("input", bnValue.toString());
      }
      else if (value != this.value) {
        this.$emit("input", value);
      }
    }
  }
};

</script>

<style scoped lang="scss">
.faded{
  color: var(--color-80)
}
</style>